import {
    EnvelopingAxis,
    EnvelopingInputFilter,
    INodeSettingsResponse,
    INodeSettingsUpdateRequest,
    LocationDefaultHardwareSettingsTemplateRequest,
    LocationDefaultHardwareSettingsTemplateResponse,
    LowFrequencyCollectionSettings,
    MagneticFieldSettings,
    ResetLocationHardwareToDefaultsRequest,
    TriggeredCollectionSettings,
    UltrasoundAmplifierMode,
    UltrasoundEnvelopingSettings,
    UltrasoundSettings,
    V4EnvelopingSettings,
    V4UpdateIntervalType,
} from '@kcftech/prometheus-client';
import {
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import {
    CollectionSetting,
    DEFAULT_COLLECTION_PERIOD_SECONDS,
    DEFAULT_ENVELOPED,
    DEFAULT_LFC_ENABLED,
    DEFAULT_TIMED,
    DEFAULT_TRIGGERED,
    defaultV4SensorSettings,
    UpdateInterval,
    V4SensorSettings,
} from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { getNodeSettingValue, updateNodeSettings } from 'utils/node';

const DEFAULT_Y_AXIS_ENVELOPING_INPUT_FILTER = EnvelopingInputFilter.Filter16kHp1000;

export const DEFAULT_ULTRASOUND_VALUES = {
    SamplingFrequency: 100_000,
    NumberSamples: 2048,
    CollectionPeriod: DEFAULT_COLLECTION_PERIOD_SECONDS,
    CenterFrequencyHz: 20_000,
    BandwidthHz: 0,
    AmplifierMode: UltrasoundAmplifierMode.Gain32Db,
};

export const DEFAULT_MAGNETIC_FIELD_VALUES = {
    SamplingFrequency: 4096,
    NumberSamples: 4096,
};

export const DEFAULT_ULTRASOUND_ENVELOPING_VALUES = {
    SamplingFrequency: 781.25,
    NumberSamples: 2000,
    InputFilter: EnvelopingInputFilter.Filter100kHp20000,
};

function collectionPeriodSeconds(
    updateInterval: UpdateInterval,
    currentSeconds: number,
): number {
    switch (updateInterval.mode) {
        case 'timed':
            return updateInterval.intervalMs / 1000;
        case 'triggered':
            return DEFAULT_COLLECTION_PERIOD_SECONDS;
        case 'smartsample':
            return currentSeconds;
        default:
            updateInterval satisfies never;
            return DEFAULT_COLLECTION_PERIOD_SECONDS;
    }
}

function mapUpdateInterval(response: INodeSettingsResponse): UpdateInterval {
    const intervalType = getNodeSettingValue(response.UpdateIntervalType);
    switch (intervalType) {
        case V4UpdateIntervalType.Timed:
            return {
                mode: 'timed',
                intervalMs:
                    getNodeSettingValue(response.UpdateInterval) ??
                    DEFAULT_TIMED.intervalMs,
            };
        case V4UpdateIntervalType.Triggered: {
            const triggered = response.TriggeredCollectionSettings;
            return {
                mode: 'triggered',
                thresholdG:
                    getNodeSettingValue(triggered?.Threshold) ??
                    DEFAULT_TRIGGERED.thresholdG,
                collectionOffsetMs:
                    getNodeSettingValue(triggered?.CollectionOffsetMilliseconds) ??
                    DEFAULT_TRIGGERED.collectionOffsetMs,
                minTimeBetweenTriggersMs:
                    getNodeSettingValue(
                        triggered?.MinimumTimeBetweenTriggersMilliseconds,
                    ) ?? DEFAULT_TRIGGERED.minTimeBetweenTriggersMs,
            };
        }
        case V4UpdateIntervalType.SmartSample:
            return { mode: 'smartsample' };
        case V4UpdateIntervalType.Invalid:
        case undefined:
            return { ...DEFAULT_TIMED };
        default:
            intervalType satisfies never;
            return { ...DEFAULT_TIMED };
    }
}

function mapLowFrequencyCollection(response: INodeSettingsResponse): CollectionSetting {
    const lfc = response.LowFrequencyCollectionSettings;
    if (lfc === undefined || getNodeSettingValue(lfc.Enabled) !== true) {
        return { enabled: false };
    }
    return {
        enabled: true,
        samplingFrequencyHz:
            getNodeSettingValue(lfc.SamplingFrequency) ??
            DEFAULT_LFC_ENABLED.samplingFrequencyHz,
        numberOfSamples:
            getNodeSettingValue(lfc.NumberSamples) ?? DEFAULT_LFC_ENABLED.numberOfSamples,
    };
}

function mapEnvelopedData(response: INodeSettingsResponse): CollectionSetting {
    const enveloping = response.EnvelopingSettings;
    if (enveloping === undefined || getNodeSettingValue(enveloping.Enabled) !== true) {
        return { enabled: false };
    }
    return {
        enabled: true,
        samplingFrequencyHz:
            getNodeSettingValue(enveloping.SamplingFrequency) ??
            DEFAULT_ENVELOPED.samplingFrequencyHz,
        numberOfSamples:
            getNodeSettingValue(enveloping.NumberSamples) ??
            DEFAULT_ENVELOPED.numberOfSamples,
    };
}

export function mapResponseToV4Settings(
    response: INodeSettingsResponse,
): V4SensorSettings {
    return {
        vibrationX: getNodeSettingValue(response.XAxisEnabled) ?? false,
        vibrationY: getNodeSettingValue(response.YAxisEnabled) ?? false,
        vibrationZ: getNodeSettingValue(response.ZAxisEnabled) ?? false,
        samplingFrequencyHz:
            getNodeSettingValue(response.SamplingFrequency) ??
            defaultV4SensorSettings.samplingFrequencyHz,
        numberOfSamples:
            getNodeSettingValue(response.NumberSamples) ??
            defaultV4SensorSettings.numberOfSamples,
        lowFrequencyCollection: mapLowFrequencyCollection(response),
        updateInterval: mapUpdateInterval(response),
        envelopedData: mapEnvelopedData(response),
        ultrasoundEnveloping:
            getNodeSettingValue(response.UltrasoundEnvelopingSettings?.Enabled) ?? false,
        turningSpeed:
            getNodeSettingValue(response.MagneticFieldSettings?.Enabled) ?? false,
        turningSpeedManuallyDisabled:
            getNodeSettingValue(response.MagneticFieldSettings?.ManuallyDisabled) ??
            false,
        collectionPeriodsSec: {
            envelopedData:
                getNodeSettingValue(response.EnvelopingSettings?.CollectionPeriod) ??
                DEFAULT_COLLECTION_PERIOD_SECONDS,
            ultrasoundEnveloping:
                getNodeSettingValue(
                    response.UltrasoundEnvelopingSettings?.CollectionPeriod,
                ) ?? DEFAULT_COLLECTION_PERIOD_SECONDS,
            turningSpeed:
                getNodeSettingValue(response.MagneticFieldSettings?.CollectionPeriod) ??
                DEFAULT_COLLECTION_PERIOD_SECONDS,
        },
    };
}

// The location-level template stores each setting flat (e.g. XAxisEnabled: boolean),
// unlike the per-node response which wraps every field in CurrentValue/PendingValue.
function mapTemplateUpdateInterval(
    response: LocationDefaultHardwareSettingsTemplateResponse,
): UpdateInterval {
    switch (response.UpdateIntervalType) {
        case V4UpdateIntervalType.Timed:
            return {
                mode: 'timed',
                intervalMs: response.UpdateInterval ?? DEFAULT_TIMED.intervalMs,
            };
        case V4UpdateIntervalType.Triggered: {
            const triggered = response.TriggeredCollectionSettings;
            return {
                mode: 'triggered',
                thresholdG: triggered?.Threshold ?? DEFAULT_TRIGGERED.thresholdG,
                collectionOffsetMs:
                    triggered?.CollectionOffsetMilliseconds ??
                    DEFAULT_TRIGGERED.collectionOffsetMs,
                minTimeBetweenTriggersMs:
                    triggered?.MinimumTimeBetweenTriggersMilliseconds ??
                    DEFAULT_TRIGGERED.minTimeBetweenTriggersMs,
            };
        }
        case V4UpdateIntervalType.SmartSample:
            return { mode: 'smartsample' };
        case V4UpdateIntervalType.Invalid:
        case undefined:
            return { ...DEFAULT_TIMED };
        default:
            response.UpdateIntervalType satisfies never;
            return { ...DEFAULT_TIMED };
    }
}

function mapTemplateLowFrequencyCollection(
    response: LocationDefaultHardwareSettingsTemplateResponse,
): CollectionSetting {
    const lfc = response.LowFrequencyCollectionSettings;
    if (lfc === undefined || lfc.Enabled !== true) {
        return { enabled: false };
    }
    return {
        enabled: true,
        samplingFrequencyHz: lfc.SamplingFrequency,
        numberOfSamples: lfc.NumberSamples,
    };
}

function mapTemplateEnvelopedData(
    response: LocationDefaultHardwareSettingsTemplateResponse,
): CollectionSetting {
    const enveloping = response.EnvelopingSettings;
    if (enveloping === undefined || enveloping.Enabled !== true) {
        return { enabled: false };
    }
    return {
        enabled: true,
        samplingFrequencyHz: enveloping.SamplingFrequency,
        numberOfSamples: enveloping.NumberSamples,
    };
}

export function mapTemplateResponseToV4Settings(
    response: LocationDefaultHardwareSettingsTemplateResponse,
): V4SensorSettings {
    return {
        vibrationX: response.XAxisEnabled ?? false,
        vibrationY: response.YAxisEnabled ?? false,
        vibrationZ: response.ZAxisEnabled ?? false,
        samplingFrequencyHz:
            response.SamplingFrequency ?? defaultV4SensorSettings.samplingFrequencyHz,
        numberOfSamples:
            response.NumberSamples ?? defaultV4SensorSettings.numberOfSamples,
        lowFrequencyCollection: mapTemplateLowFrequencyCollection(response),
        updateInterval: mapTemplateUpdateInterval(response),
        envelopedData: mapTemplateEnvelopedData(response),
        ultrasoundEnveloping: response.UltrasoundEnvelopingSettings?.Enabled ?? false,
        turningSpeed: response.MagneticFieldSettings?.Enabled ?? false,
        turningSpeedManuallyDisabled:
            response.MagneticFieldSettings?.ManuallyDisabled ?? false,
        collectionPeriodsSec: {
            envelopedData:
                response.EnvelopingSettings?.CollectionPeriod ??
                DEFAULT_COLLECTION_PERIOD_SECONDS,
            ultrasoundEnveloping:
                response.UltrasoundEnvelopingSettings?.CollectionPeriod ??
                DEFAULT_COLLECTION_PERIOD_SECONDS,
            turningSpeed:
                response.MagneticFieldSettings?.CollectionPeriod ??
                DEFAULT_COLLECTION_PERIOD_SECONDS,
        },
    };
}

// Lists fields a configured V4 sensor should send; a gap means the mapper fabricates
// a default that gets saved back.
export function missingV4SettingFields(response: INodeSettingsResponse): string[] {
    const missing: string[] = [];
    if (getNodeSettingValue(response.XAxisEnabled) === undefined) {
        missing.push('XAxisEnabled');
    }
    if (getNodeSettingValue(response.YAxisEnabled) === undefined) {
        missing.push('YAxisEnabled');
    }
    if (getNodeSettingValue(response.ZAxisEnabled) === undefined) {
        missing.push('ZAxisEnabled');
    }
    if (getNodeSettingValue(response.SamplingFrequency) === undefined) {
        missing.push('SamplingFrequency');
    }
    if (getNodeSettingValue(response.NumberSamples) === undefined) {
        missing.push('NumberSamples');
    }
    if (
        getNodeSettingValue(response.UltrasoundEnvelopingSettings?.Enabled) === undefined
    ) {
        missing.push('UltrasoundEnvelopingSettings.Enabled');
    }
    if (getNodeSettingValue(response.MagneticFieldSettings?.Enabled) === undefined) {
        missing.push('MagneticFieldSettings.Enabled');
    }

    const updateIntervalType = getNodeSettingValue(response.UpdateIntervalType);
    if (updateIntervalType === undefined) {
        missing.push('UpdateIntervalType');
    }
    if (
        updateIntervalType === V4UpdateIntervalType.Timed &&
        getNodeSettingValue(response.UpdateInterval) === undefined
    ) {
        missing.push('UpdateInterval');
    }

    const lfc = response.LowFrequencyCollectionSettings;
    if (lfc !== undefined && getNodeSettingValue(lfc.Enabled) === true) {
        if (getNodeSettingValue(lfc.SamplingFrequency) === undefined) {
            missing.push('LowFrequencyCollectionSettings.SamplingFrequency');
        }
        if (getNodeSettingValue(lfc.NumberSamples) === undefined) {
            missing.push('LowFrequencyCollectionSettings.NumberSamples');
        }
    }

    const enveloping = response.EnvelopingSettings;
    if (enveloping !== undefined && getNodeSettingValue(enveloping.Enabled) === true) {
        if (getNodeSettingValue(enveloping.SamplingFrequency) === undefined) {
            missing.push('EnvelopingSettings.SamplingFrequency');
        }
        if (getNodeSettingValue(enveloping.NumberSamples) === undefined) {
            missing.push('EnvelopingSettings.NumberSamples');
        }
    }

    const triggered = response.TriggeredCollectionSettings;
    if (updateIntervalType === V4UpdateIntervalType.Triggered) {
        if (getNodeSettingValue(triggered?.Threshold) === undefined) {
            missing.push('TriggeredCollectionSettings.Threshold');
        }
        if (getNodeSettingValue(triggered?.CollectionOffsetMilliseconds) === undefined) {
            missing.push('TriggeredCollectionSettings.CollectionOffsetMilliseconds');
        }
        if (
            getNodeSettingValue(triggered?.MinimumTimeBetweenTriggersMilliseconds) ===
            undefined
        ) {
            missing.push(
                'TriggeredCollectionSettings.MinimumTimeBetweenTriggersMilliseconds',
            );
        }
    }

    return missing;
}

const UPDATE_INTERVAL_TYPE_BY_MODE: Record<UpdateInterval['mode'], V4UpdateIntervalType> =
    {
        smartsample: V4UpdateIntervalType.SmartSample,
        triggered: V4UpdateIntervalType.Triggered,
        timed: V4UpdateIntervalType.Timed,
    };

export type V4SettingsRequestFields = Pick<
    LocationDefaultHardwareSettingsTemplateRequest,
    | 'XAxisEnabled'
    | 'YAxisEnabled'
    | 'ZAxisEnabled'
    | 'SamplingFrequency'
    | 'NumberSamples'
    | 'LowFrequencyCollectionSettings'
    | 'EnvelopingSettings'
    | 'UpdateIntervalType'
    | 'UpdateInterval'
    | 'TriggeredCollectionSettings'
    | 'UltrasoundSettings'
    | 'UltrasoundEnvelopingSettings'
    | 'MagneticFieldSettings'
>;

// Guard: a node update request must stay assignable to the template field subset so the
// shared writer keeps type-checking for both save paths. If the SDK ever retypes a shared
// field, this fails to compile here at the Pick rather than at the distant call site.
const v4FieldsAreShared = (
    request: INodeSettingsUpdateRequest,
): V4SettingsRequestFields => request;
void v4FieldsAreShared;

// Always send Enabled plus freq/samples; a disabled collection falls back to the defaults so
// omitting them can't leave a prior enabled state in place on the node.
function collectionSettingFields(
    collection: CollectionSetting,
    fallback: Extract<CollectionSetting, { enabled: true }>,
) {
    return {
        Enabled: collection.enabled,
        SamplingFrequency: collection.enabled
            ? collection.samplingFrequencyHz
            : fallback.samplingFrequencyHz,
        NumberSamples: collection.enabled
            ? collection.numberOfSamples
            : fallback.numberOfSamples,
    };
}

export function applyV4SettingsToRequest(
    request: V4SettingsRequestFields,
    settings: V4SensorSettings,
): void {
    request.XAxisEnabled = settings.vibrationX;
    request.YAxisEnabled = settings.vibrationY;
    request.ZAxisEnabled = settings.vibrationZ;
    request.SamplingFrequency = settings.samplingFrequencyHz;
    request.NumberSamples = settings.numberOfSamples;

    request.LowFrequencyCollectionSettings = new LowFrequencyCollectionSettings(
        collectionSettingFields(settings.lowFrequencyCollection, DEFAULT_LFC_ENABLED),
    );
    request.EnvelopingSettings = new V4EnvelopingSettings({
        ...collectionSettingFields(settings.envelopedData, DEFAULT_ENVELOPED),
        Axis: EnvelopingAxis.Y,
        InputFilter: DEFAULT_Y_AXIS_ENVELOPING_INPUT_FILTER,
        CollectionPeriod: collectionPeriodSeconds(
            settings.updateInterval,
            settings.collectionPeriodsSec.envelopedData,
        ),
    });

    request.UpdateIntervalType =
        UPDATE_INTERVAL_TYPE_BY_MODE[settings.updateInterval.mode];
    request.UpdateInterval =
        settings.updateInterval.mode === 'timed'
            ? settings.updateInterval.intervalMs
            : undefined;

    request.TriggeredCollectionSettings =
        settings.updateInterval.mode === 'triggered'
            ? new TriggeredCollectionSettings({
                  Threshold: settings.updateInterval.thresholdG,
                  CollectionOffsetMilliseconds:
                      settings.updateInterval.collectionOffsetMs,
                  MinimumTimeBetweenTriggersMilliseconds:
                      settings.updateInterval.minTimeBetweenTriggersMs,
              })
            : undefined;

    request.UltrasoundSettings = new UltrasoundSettings({
        Enabled: false,
        ...DEFAULT_ULTRASOUND_VALUES,
    });
    request.UltrasoundEnvelopingSettings = new UltrasoundEnvelopingSettings({
        Enabled: settings.ultrasoundEnveloping,
        ...DEFAULT_ULTRASOUND_ENVELOPING_VALUES,
        CollectionPeriod: collectionPeriodSeconds(
            settings.updateInterval,
            settings.collectionPeriodsSec.ultrasoundEnveloping,
        ),
    });
    request.MagneticFieldSettings = new MagneticFieldSettings({
        Enabled: settings.turningSpeed,
        ManuallyDisabled: settings.turningSpeedManuallyDisabled,
        ...DEFAULT_MAGNETIC_FIELD_VALUES,
        CollectionPeriod: collectionPeriodSeconds(
            settings.updateInterval,
            settings.collectionPeriodsSec.turningSpeed,
        ),
    });
}

type V4SensorSettingsQueryConfig = {
    locationId: string;
};

export function useV4SensorSettings({ locationId }: V4SensorSettingsQueryConfig) {
    return useQuery(
        queryOptions({
            queryKey: ['v4-sensor-settings', locationId] as const,
            queryFn: async (): Promise<V4SensorSettings> => {
                const response =
                    await sdClient.getLocationDefaultHardwareSettingsTemplateByLocation({
                        locationId,
                    });
                return mapTemplateResponseToV4Settings(response);
            },
        }),
    );
}

export type UpdateV4SensorSettingsInput = Readonly<{
    nodeId: number;
    settings: V4SensorSettings;
}>;

// The caller refreshes the node settings on success (see V4EditSettingsModal onSaved).
export function useUpdateV4SensorSettings() {
    return useMutation({
        meta: { name: 'v4-node-settings' },
        mutationKey: ['v4-node-settings', 'update'],
        mutationFn: ({ nodeId, settings }: UpdateV4SensorSettingsInput) =>
            updateNodeSettings(nodeId, (request) =>
                applyV4SettingsToRequest(request, settings),
            ),
    });
}

export type ResetNodeToLocationDefaultsInput = Readonly<{
    locationId: string;
    nodeId: number;
}>;

// Resets a single node to its location default hardware settings. The backend applies the
// location defaults and clears the node's IndividuallyConfigured flag. Caller refreshes the
// node on success.
export function useResetNodeToLocationDefaults() {
    return useMutation({
        meta: { name: 'v4-node-settings' },
        mutationKey: ['v4-node-settings', 'reset'],
        mutationFn: ({ locationId, nodeId }: ResetNodeToLocationDefaultsInput) =>
            sdClient.resetLocationHardwareToDefaults({
                body: new ResetLocationHardwareToDefaultsRequest({
                    LocationId: locationId,
                    NodeIds: [nodeId],
                }),
            }),
    });
}

export type UpdateLocationV4SensorSettingsInput = Readonly<{
    locationId: string;
    settings: V4SensorSettings;
}>;

// Reads the template back first to choose update vs. create: a saved template has a real
// (positive) Id, while a location with none yet comes back as an empty template with Id 0
// (and a zero-GUID LocationId), which is the create path.
export function useUpdateLocationV4SensorSettings() {
    const queryClient = useQueryClient();
    return useMutation({
        meta: { name: 'v4-location-settings' },
        mutationKey: ['v4-location-settings', 'update'],
        mutationFn: async ({
            locationId,
            settings,
        }: UpdateLocationV4SensorSettingsInput) => {
            const existing =
                await sdClient.getLocationDefaultHardwareSettingsTemplateByLocation({
                    locationId,
                });
            const body = new LocationDefaultHardwareSettingsTemplateRequest({
                LocationId: locationId,
            });
            applyV4SettingsToRequest(body, settings);

            return existing.Id
                ? sdClient.updateLocationDefaultHardwareSettingsTemplate({
                      nodeSettingsTemplateId: existing.Id,
                      body,
                  })
                : sdClient.createLocationDefaultHardwareSettingsTemplate({ body });
        },
        onSuccess: (_result, { locationId }) => {
            void queryClient.invalidateQueries({
                queryKey: ['v4-sensor-settings', locationId],
            });
        },
    });
}
